import { Component, computed, DestroyRef, ElementRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DealOption } from '../../../../models/deal-options-view.models';
import { AddendumLogsComponent } from '../../addendum-logs/addendum-logs.component';
import { NgbDropdownModule, NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import {
  AbilityService,
  AddendumType,
  ConfirmHelperService,
  CurrencyFormatPipe,
  DropdownData,
  GenericDropdownComponent,
  NgbModalService,
  SvgIconsComponent
} from '@zayan-erp/shared';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DealOptionsService } from '../../../../services/deal-options.service';
import { DatePipe } from '@angular/common';
import { SalesApsService } from 'libs/sales/src/lib/services/sales-aps.service';
import { BuilderReferenceService } from '@zayan-erp/home-building';
import { map } from 'rxjs';

@Component({
  selector: 'deal-addendum-preview',
  imports: [
    AddendumLogsComponent,
    ConfirmDialogModule,
    TooltipModule,
    CurrencyFormatPipe,
    DatePipe,
    SvgIconsComponent,
    NgbDropdownModule,
    NgbPopover,
    ReactiveFormsModule,
    GenericDropdownComponent
  ],
  providers: [],
  templateUrl: './deal-addendum-preview.component.html'
})
export class DealAddendumPreviewComponent {
  @ViewChild('optionLogsPopupEl') optionLogsPopupEl!: ElementRef;
  @ViewChild('editBuilderReferencePopupEl') editBuilderReferencePopupEl!: ElementRef;
  @ViewChild('tooltipContent') tooltipContent!: any;

  private readonly modalService = inject(NgbModal);
  private readonly ngbModelService = inject(NgbModalService);
  private readonly confirmationService = inject(ConfirmHelperService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dealOptionsService = inject(DealOptionsService);
  private readonly salesApsService = inject(SalesApsService);
  private readonly abilityService = inject(AbilityService);
  AddendumType = AddendumType;
  private readonly builderReferenceService = inject(BuilderReferenceService);
  private readonly fb = inject(FormBuilder);

  readonly dealAddendum = input.required<DealOption>();

  onChanges = output<void>();

  readonly isSubmittingBuilderReference = signal(false);

  readonly builderReferences = toSignal(
    this.builderReferenceService
      .getBuilderReferences()
      .pipe(map(references => references.map<DropdownData>(ref => ({ id: ref.id, firstLabel: ref.reference })))),
    { initialValue: [] }
  );

  readonly builderReferenceForm = this.fb.group({
    builderReference: [null as string | null, Validators.required]
  });

  // Totals from backend
  readonly discountsTotal = computed(() => this.dealAddendum().discountsTotal ?? 0);
  readonly optionsTotal = computed(() => this.dealAddendum().optionsTotal ?? 0);
  readonly incentivesTotal = computed(() => this.dealAddendum().incentivesTotal ?? 0);
  readonly taxesTotal = computed(() => this.dealAddendum().taxesTotal ?? 0);
  readonly isLocked = computed(() => this.dealAddendum().isLocked);
  /** Single source of truth for this component's write gate. */
  readonly canWriteDeals = computed(() => this.abilityService.ability.can('write-sales-deals', 'deals'));
  readonly canDelete = computed(() => !this.isLocked() && this.canWriteDeals());
  readonly canCancel = computed(
    () =>
      this.isLocked() &&
      !this.dealAddendum().isCancelled &&
      this.abilityService.ability.can('cancel-locked-addendum', 'deals') &&
      this.canWriteDeals()
  );
  // SuperAdmin-only when locked; otherwise available to anyone who can edit the addendum.
  readonly canEditBuilderReference = computed(
    () =>
      !this.dealAddendum().isCancelled &&
      (!this.isLocked() || this.abilityService.ability.can('edit-locked-addendum', 'deals'))
  );

  openOptionLogsModal(): void {
    this.modalService.open(this.optionLogsPopupEl, this.ngbModelService.centerModal);
  }

  openEditBuilderReferenceModal(event: Event): void {
    event.stopPropagation();
    this.builderReferenceForm.patchValue({
      builderReference: this.dealAddendum().builderReference.id
    });
    this.modalService.open(this.editBuilderReferencePopupEl, this.ngbModelService.centerModal);
  }

  saveBuilderReference(modal: { close: () => void }): void {
    if (this.builderReferenceForm.invalid) {
      this.builderReferenceForm.markAllAsTouched();
      return;
    }

    const addendum = this.dealAddendum();
    const builderReferenceId = this.builderReferenceForm.controls.builderReference.value;
    if (!builderReferenceId) {
      return;
    }

    if (builderReferenceId === addendum.builderReference.id) {
      modal.close();
      return;
    }

    this.isSubmittingBuilderReference.set(true);
    this.dealOptionsService
      .updateBuilderReference(addendum.id, builderReferenceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmittingBuilderReference.set(false);
          modal.close();
          this.onChanges.emit();
        }
      });
  }

  showConfirmation(action: 'cancel' | 'delete') {
    const isDelete = action === 'delete';
    this.confirmationService
      .showModal({
        icon: 'confirm-icon',
        header: isDelete ? 'Delete Addendum?' : 'Cancel Addendum?',
        message: isDelete
          ? 'Do you want to delete this addendum? This action is permanent and cannot be undone.'
          : 'Do you want to cancel this addendum? This action is permanent and cannot be undone.',
        acceptLabel: isDelete ? 'Delete' : 'Cancel Addendum',
        acceptSeverity: isDelete ? 'danger' : 'primary',
        rejectLabel: 'No'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          isDelete ? this.deleteOption() : this.cancelAddendum();
        }
      });
  }

  deleteOption(): void {
    if (!this.canWriteDeals()) return;
    this.dealOptionsService
      .deleteOption(this.dealAddendum().id, {
        lotId: this.dealAddendum().lotId,
        dealId: this.salesApsService.currentDeal()?.id
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.onChanges.emit();
        }
      });
  }

  cancelAddendum(): void {
    if (!this.canWriteDeals()) return;
    this.dealOptionsService
      .cancelAddendum(this.dealAddendum().id, {
        lotId: this.dealAddendum().lotId,
        dealId: this.salesApsService.currentDeal()?.id
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.salesApsService.realoadDeal.next(null);
          this.onChanges.emit();
        }
      });
  }
}
